/**
 * Password Validation Utilities
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
 * 
 * Validates passwords against Cognito requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 lowercase letter (a-z)
 * - At least 1 digit (0-9)
 * - At least 1 special character
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface PasswordConfirmationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate password against Cognito requirements
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  // Requirement 2.1: Minimum 8 characters
  if (password.length < 8) {
    errors.push('パスワードは8文字以上で入力してください');
  }

  // Requirement 2.2: At least 1 uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('大文字を1文字以上含めてください');
  }

  // Requirement 2.3: At least 1 lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('小文字を1文字以上含めてください');
  }

  // Requirement 2.4: At least 1 digit
  if (!/\d/.test(password)) {
    errors.push('数字を1文字以上含めてください');
  }

  // Requirement 2.5: At least 1 special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('特殊文字を1文字以上含めてください');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate password confirmation matches
 * Requirement 2.6
 */
export function validatePasswordConfirmation(
  password: string,
  confirmPassword: string
): PasswordConfirmationResult {
  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'パスワードが一致しません',
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Combined validation for password and confirmation
 * Requirement 2.7: All validation passes → submit enabled
 */
export function validatePasswordForm(
  password: string,
  confirmPassword: string
): { isValid: boolean; passwordErrors: string[]; confirmError?: string } {
  const passwordResult = validatePassword(password);
  const confirmResult = validatePasswordConfirmation(password, confirmPassword);

  return {
    isValid: passwordResult.isValid && confirmResult.isValid,
    passwordErrors: passwordResult.errors,
    confirmError: confirmResult.error,
  };
}
